import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot } from '@angular/router';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { CaseGroup } from '../../../api/app-types/case-group';
import { ContactPointCategory } from '../../../api/app-types/contact-point.app';
import { ContactType } from '../../../api/app-types/contact-type';
import { County } from '../../../api/app-types/county.app';
import { LekCategories, LekDateRangePossibilities } from '../../../api/app-types/lek.app';
import { PersonType } from '../../../api/app-types/person-type.app';
import { PERSON_TYPE } from '../../../api/app-types/search.app';
import { TypeOfCare } from '../../../api/app-types/type-of-care';
import { CATEGORIES_API } from '../../../api/injection-tokens/services.token';
import { LEK_PROVIDER } from '../../../api/services/lek.service';
import { transformApiGetLekCategories } from '../../../api/transformers/lek.transform';
import { PaginatedResponse } from '../../pagination/paginated-response';

export const contactPointCategoriesResolver: ResolveFn<PaginatedResponse<ContactPointCategory>> = async (
	_route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(CATEGORIES_API);
	const response = await api.getAllContactPointTypes();
	return response;
};

export const personTypesResolver: ResolveFn<PaginatedResponse<PersonType>> = async (
	_route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(CATEGORIES_API);
	const env = inject(ENVIRONMENT);
	const response = await api.getAllPersonTypes(
		env.pagination.offset,
		env.pagination.range,
		undefined,
		PERSON_TYPE.INSTITUTION_PERSON,
	);
	return response;
};

export const personTypeInstitutionsResolver: ResolveFn<PaginatedResponse<PersonType>> = async (
	_route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(CATEGORIES_API);
	const env = inject(ENVIRONMENT);
	const response = await api.getAllPersonTypes(env.pagination.offset, env.pagination.range, 'IsInitiator');
	return response;
};

export const categoryPersonCategoriesResolver: ResolveFn<PaginatedResponse<PersonType>> = async (
	_route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(CATEGORIES_API);
	const env = inject(ENVIRONMENT);
	const response = await api.getAllPersonTypes(
		env.pagination.offset,
		env.pagination.range,
		undefined,
		PERSON_TYPE.RELATIVES,
	);
	return response;
};

export const countiesResolver: ResolveFn<PaginatedResponse<County>> = async (
	_route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(CATEGORIES_API);
	const env = inject(ENVIRONMENT);
	const response = await api.getAllCounties(env.pagination.offset, env.pagination.range, 'IsResidenceDistrict');
	return response;
};

export const typesOfCareResolver: ResolveFn<PaginatedResponse<TypeOfCare>> = async (
	_route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(CATEGORIES_API);
	const env = inject(ENVIRONMENT);
	const response = await api.getAllTypesOfCare(env.pagination.offset, env.pagination.range, false, false, true);
	return {
		entities: response.typesOfCare,
		totalEntities: response.totalTypesOfCare,
		offset: env.pagination.offset,
		range: env.pagination.range,
	};
};

export const caseGroupsResolver: ResolveFn<PaginatedResponse<CaseGroup>> = async (
	_route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(CATEGORIES_API);
	const env = inject(ENVIRONMENT);
	const response = await api.getAllCaseGroups(env.pagination.offset, env.pagination.range);
	return {
		entities: response.caseGroups,
		totalEntities: response.totalCaseGroups,
		offset: env.pagination.offset,
		range: env.pagination.range,
	};
};

export const contactTypesResolver: ResolveFn<PaginatedResponse<ContactType>> = async (
	_route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(CATEGORIES_API);
	const env = inject(ENVIRONMENT);
	const response = await api.getAllContactTypes(env.pagination.offset, env.pagination.range);
	return {
		entities: response.contactTypes,
		totalEntities: response.totalContactTypes,
		offset: env.pagination.offset,
		range: env.pagination.range,
	};
};

export const lekCategoriesResolver: ResolveFn<LekCategories> = async (
	route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(CATEGORIES_API);
	const assignmentId = Number(route.paramMap.get('assignmentId'));
	const apiResponse = await api.getAllCategoryAssistance(assignmentId);
	const transformed = transformApiGetLekCategories(apiResponse);
	return transformed;
};

export const dateRangeResolver: ResolveFn<LekDateRangePossibilities> = async (
	route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(LEK_PROVIDER);
	const clientId = Number(route.paramMap.get('id'));
	const response = await api.getValidDateRanges(clientId);
	return response;
};
